import React, { useState } from 'react';
import {
  Canva,
  Giphy,
  Unsplash,
  Dropbox,
  GoogleDrive,
  Onedrive,
  GooglePhotos,
} from '@bufferapp/ui/Icon';
import { useLazyQuery } from '@apollo/client';
import { getCanvaDesignConfig, openCanvaModal } from '~/canva/handlers';

import { fileFromUrl } from '~/uploads/lib/remoteFile';
import { OnFileReadyCb } from '~/uploads/types';
import { useIsIntegrationUploading } from '~/integrations-bar/state/useIsIntegrationUploading';
import { useAppDispatch } from '~/store';
import { addPermanentNotification } from '~/notifications-provider/notificationsSlice';

import { logError } from '~/utils/logError';
import {
  CANVA_SERVICE,
  GIPHY_SERVICE,
  UNSPLASH_SERVICE,
  DROPBOX_SERVICE,
  GOOGLE_DRIVE_SERVICE,
  GOOGLE_PHOTOS_SERVICE,
  ONE_DRIVE_SERVICE,
} from './constants';
import { GraphImage, IntegrationsContext, IntegrationsService } from './types';
import { openUploadcareModal } from '../uploadcare/openUploadcareModal';

import {
  UploadcareIntegrations,
  UploadcareSources,
} from '../uploadcare/constants';

import {
  BUTTON_CLICKED,
  MEDIA_ATTACHED,
  sendIntegrationsEvent,
} from './tracking';

import { TRACK_IMAGES_QUERY } from './queries';
/*
 * HOOK: useIntegrationServices
 */
export const useIntegrationServices = ({
  context,
  onFileReady,
  updateType,
  serviceName = 'omni',
  onError,
  enableCanva = true,
  enableGiphy = true,
  enableUnsplash = true,
}: {
  context: IntegrationsContext;
  onFileReady: OnFileReadyCb;
  updateType?: string;
  serviceName?: string;
  onError?: ({ message }: { message: string }) => void;
  enableCanva?: boolean;
  enableGiphy?: boolean;
  enableUnsplash?: boolean;
}): {
  services: Array<IntegrationsService>;
  activeService: IntegrationsService | null;
} => {
  const dispatch = useAppDispatch();
  const [activeService, setActiveService] =
    useState<IntegrationsService | null>(null);

  const serviceClose = () => {
    setActiveService(null);
  };

  const serviceOpen = (service: IntegrationsService) => {
    service.onClose = serviceClose;
    setActiveService(service);
    sendIntegrationsEvent(BUTTON_CLICKED, context, service.id);

    service.onClick?.();
  };

  const { onUploadStarted, onUploadFinished } = useIsIntegrationUploading();

  const [trackImages] = useLazyQuery(TRACK_IMAGES_QUERY);

  if (!onError) {
    onError = ({ message }: { message: string }) => {
      dispatch(addPermanentNotification(`⚠️ ${message}`));
    };
  }

  /*
   * uploadMediaFile
   *
   * Fetchs the file from url, calls onFileReady function (Uploader tasks associated to the Editor),
   * sends MEDIA_ATTACHED event for integrations and set activeService=null
   */
  const uploadMediaFile = async (
    url: string,
    service: string,
    name?: string,
    downloadUrl?: string
  ) => {
    const remoteFile = {
      remoteUrl: url,
      name,
    };
    try {
      onUploadStarted();
      serviceClose();
      const fileSelected = await fileFromUrl(remoteFile);
      onFileReady(fileSelected);
      onUploadFinished();
      sendIntegrationsEvent(
        MEDIA_ATTACHED,
        context,
        service,
        fileSelected?.type
      );

      if (service === UNSPLASH_SERVICE) {
        trackImages({
          variables: {
            trackUrl: downloadUrl,
          },
        });
      }
    } catch (error) {
      logError(error, { metaData: { integration: service, remoteFile } });
      onError?.({ message: `Failed to upload media ${error.message}` });
    }
  };

  const openUploadCare = (integration: string) => {
    openUploadcareModal(
      serviceName,
      onFileReady,
      onError,
      integration,
      onUploadStarted,
      onUploadFinished,
      UploadcareSources.BAR,
      null,
      updateType,
      context
    );
  };

  // INCLUDES HERE THE SERVICES FOR THE INTEGRATIONS BAR
  const services: IntegrationsService[] = [
    {
      id: CANVA_SERVICE,
      title: 'Canva',
      label: 'Design with Canva',
      placeholder: '', // not required for canva
      isNew: false,
      isEnabled: enableCanva,
      icon: Canva,
      openSearchImagesModal: false,
      onClick: () => {
        openCanvaModal({
          onFileReady,
          designConfig: getCanvaDesignConfig(serviceName),
          onClose: serviceClose,
        });
      },
    },
    {
      id: DROPBOX_SERVICE,
      title: 'Dropbox',
      label: 'Select from Dropbox',
      isNew: false,
      isEnabled: true,
      icon: Dropbox,
      openSearchImagesModal: false,
      onClick: () => {
        openUploadCare(UploadcareIntegrations.DROPBOX);
      },
    },
    {
      id: GIPHY_SERVICE,
      title: 'Giphy',
      label: 'Select from Giphy',
      placeholder: 'Search Gifs',
      isNew: false,
      isEnabled: enableGiphy,
      icon: Giphy,
      openSearchImagesModal: true,
      footer: (
        <img
          src="https://buffer-publish.s3.amazonaws.com/images/logo-giphy.png"
          alt="Select Gifs from Giphy"
          width={120}
        />
      ),
      onFileSelected: async (imageTarget: GraphImage) => {
        uploadMediaFile(imageTarget.regularUrl, GIPHY_SERVICE);
      },
    },
    {
      id: GOOGLE_DRIVE_SERVICE,
      title: 'Google Drive',
      label: 'Select from Google Drive',
      isNew: false,
      isEnabled: true,
      icon: GoogleDrive,
      openSearchImagesModal: false,
      onClick: () => {
        openUploadCare(UploadcareIntegrations.GOOGLE_DRIVE);
      },
    },
    {
      id: GOOGLE_PHOTOS_SERVICE,
      title: 'Google Photos',
      label: 'Select from Google Photos',
      isNew: false,
      isEnabled: true,
      icon: GooglePhotos,
      openSearchImagesModal: false,
      onClick: () => {
        openUploadCare(UploadcareIntegrations.GOOGLE_PHOTOS);
      },
    },

    {
      id: ONE_DRIVE_SERVICE,
      title: 'One Drive',
      label: 'Select from OneDrive',
      isNew: false,
      isEnabled: true,
      icon: Onedrive,
      openSearchImagesModal: false,
      onClick: () => {
        openUploadCare(UploadcareIntegrations.ONE_DRIVE);
      },
    },
    {
      id: UNSPLASH_SERVICE,
      title: 'Unsplash',
      label: 'Select from Unsplash',
      placeholder: 'Search free high resolution photos',
      isNew: true,
      isEnabled: enableUnsplash,
      icon: Unsplash,
      openSearchImagesModal: true,
      onFileSelected: async (imageTarget: GraphImage) => {
        uploadMediaFile(
          imageTarget.regularUrl,
          UNSPLASH_SERVICE,
          `${imageTarget.id}-unsplash.jpg`,
          imageTarget.downloadUrl
        );
      },
    },
  ];

  return {
    services: services
      .filter((s) => s.isEnabled)
      .map((s) => {
        return {
          ...s,
          onClick: () => serviceOpen(s),
        };
      }),
    activeService,
  };
};
